/**
 * Barcode decoding with a fallback for browsers that have no BarcodeDetector
 * (iOS Safari, Firefox).
 *
 * Native first: where `window.BarcodeDetector` exists and reports support for the
 * formats we need, it is used and **the wasm bundle is never fetched**. Only on
 * the fallback path is `zxing-wasm` imported, and it is a dynamic import so it
 * lands in its own chunk rather than the main bundle.
 *
 * Returns the raw decoded string only. GS1 parsing stays in the backend
 * (`receiving/goods_in_capture.parse_gs1`), so nothing here interprets an
 * Application Identifier.
 */

/** Formats we ask for. Names match the BarcodeDetector vocabulary. */
export const SUPPORTED_FORMATS = Object.freeze([
  'code_128',
  'ean_13',
  'ean_8',
  'data_matrix',
]);

/**
 * GS1-128 is Code 128 carrying GS1 Application Identifiers, not a separate
 * symbology, so it is requested as `code_128`. Documented here because the task
 * lists GS1-128 as a format and there is no native name for it.
 */
export const GS1_128_ALIAS = 'code_128';

export function nativeDetectorAvailable() {
  return typeof window !== 'undefined' && typeof window.BarcodeDetector === 'function';
}

/** Which of our formats the native detector supports, or [] when there is none. */
export async function nativeSupportedFormats() {
  if (!nativeDetectorAvailable()) return [];
  try {
    const formats = await window.BarcodeDetector.getSupportedFormats();
    return Array.isArray(formats) ? formats : [];
  } catch {
    return [];
  }
}

/**
 * True when the native detector covers every format we need.
 *
 * The `supported.length` guard matters: `[].every(...)` is vacuously true, so
 * without it a browser with no BarcodeDetector at all would be reported as
 * having full native support.
 */
export async function canUseNative() {
  const supported = await nativeSupportedFormats();
  if (supported.length === 0) return false;
  return SUPPORTED_FORMATS.every((f) => supported.includes(f));
}

/**
 * Decode one frame. Returns the raw string, or null when nothing was found.
 * Throws only when neither path is usable, so the caller can show its own message.
 */
export async function decode(source, { fallback } = {}) {
  if (await canUseNative()) {
    const detector = new window.BarcodeDetector({ formats: [...SUPPORTED_FORMATS] });
    const found = await detector.detect(source);
    const first = (found || [])[0];
    return first ? first.rawValue : null;
  }
  // `fallback` is a test seam; production always uses the zxing path below.
  const fallbackFn = fallback || decodeWithZxing;
  return fallbackFn(source);
}

/**
 * The fallback. `zxing-wasm` is imported lazily so its wasm payload is a separate
 * chunk that only downloads on a browser without BarcodeDetector.
 */
export async function decodeWithZxing(source) {
  const mod = await import('zxing-wasm/reader');
  // zxing-wasm defaults to fetching its .wasm from the jsDelivr CDN. Scanning in a
  // basement with no signal is the whole point of this feature, so point it at the
  // copy this app serves from its own origin (see scripts/copy-zxing-wasm.js).
  if (typeof mod.prepareZXingModule === 'function') {
    await mod.prepareZXingModule({
      overrides: {
        locateFile: (file) => `/vendor/zxing/${file}`,
      },
      fireImmediately: true,
    });
  }
  // A dynamic import may hand back the module namespace or its `default`,
  // depending on how the bundler/Jest interops a CJS package.
  const readBarcodes = mod.readBarcodes
    || (mod.default && mod.default.readBarcodes);
  if (typeof readBarcodes !== 'function') {
    throw new Error('Barcode fallback unavailable: zxing-wasm did not load');
  }
  const results = await readBarcodes(source, {
    formats: ['Code128', 'EAN-13', 'EAN-8', 'DataMatrix'],
    tryHarder: true,
  });
  const hit = (results || []).find((r) => r && r.text);
  return hit ? hit.text : null;
}

/**
 * Convenience for a <video> element: keeps decoding until a code appears or the
 * caller's signal aborts. Mirrors the polling loop the call sites used.
 */
export async function decodeVideo(video, { intervalMs = 250, signal } = {}) {
  if (!video) return null;
  const stopped = () => signal && signal.aborted;
  // eslint-disable-next-line no-constant-condition
  while (!stopped()) {
    const value = await decode(video);
    if (value) return value;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return null;
}
